/**
 * RbacGate/index.js
 *
 * Gate wrapper + useRbac hook.
 *
 * Usage:
 *   <RbacGate screenId="account-subscription">
 *     <MyScreen />
 *   </RbacGate>
 *
 * If the current user lacks the roles required for `screenId` (as defined
 * in rbac.json), an "Access Restricted" full-page message is rendered in
 * place of children.
 *
 * The hook re-evaluates on:
 *   - pos:rolesLoaded      — roles fetch for the current location completed
 *   - pos:locationChanging — location switch begins; enter loading state to
 *                            avoid an "Access Restricted" flash while roles
 *                            are being fetched for the new location
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { canViewScreen } from "../../services/rbacConfig";
import "./index.css";

/* ── Lock icon ──────────────────────────────────────────── */
function LockIcon() {
  return (
    <svg
      className="rg-lock-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/* ── Loading skeleton shown during location-switch transition ── */
function RolesLoadingSpinner() {
  return (
    <div className="rg-loading">
      <div className="rg-spinner" aria-label="Loading…" />
    </div>
  );
}

/* ── useRbac hook ───────────────────────────────────────── */
export function useRbac(screenId) {
  const [allowed, setAllowed] = useState(() => canViewScreen(screenId));
  // loading = true while location switch is in progress (roles not yet fetched)
  const [loading, setLoading] = useState(false);
  // Safety timer ref — cancelled when pos:rolesLoaded fires normally
  const safetyTimerRef = useRef(null);

  useEffect(() => {
    const clearSafetyTimer = () => {
      if (safetyTimerRef.current !== null) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
    };

    const onChanging = () => {
      setLoading(true);
      // optimistically keep current allowed value; AccessDenied won't show

      // Safety net: if pos:rolesLoaded never fires (network error / timeout),
      // fall back to whatever is in localStorage after 9 seconds so the
      // spinner never stays visible indefinitely.
      clearSafetyTimer();
      safetyTimerRef.current = setTimeout(() => {
        safetyTimerRef.current = null;
        setAllowed(canViewScreen(screenId));
        setLoading(false);
      }, 9000);
    };

    const onLoaded = () => {
      clearSafetyTimer();
      setAllowed(canViewScreen(screenId));
      setLoading(false);
    };

    window.addEventListener("pos:locationChanging", onChanging);
    window.addEventListener("pos:rolesLoaded", onLoaded);
    return () => {
      clearSafetyTimer();
      window.removeEventListener("pos:locationChanging", onChanging);
      window.removeEventListener("pos:rolesLoaded", onLoaded);
    };
  }, [screenId]);

  return { allowed, loading };
}

/* ── Access-denied page ─────────────────────────────────── */
function AccessDenied() {
  const navigate = useNavigate();
  return (
    <div className="rg-denied">
      <LockIcon />
      <h2 className="rg-denied-title">Access Restricted</h2>
      <p className="rg-denied-body">
        You don&apos;t have permission to view this screen. Contact your Location Admin or
        Organisation Admin for access.
      </p>
      <button
        className="rg-denied-btn"
        onClick={() => {
          if (window.history.length > 1) navigate(-1);
          else navigate("/products/operations-menu");
        }}
      >
        Go Back
      </button>
    </div>
  );
}

/* ── RbacGate wrapper ───────────────────────────────────── */
export default function RbacGate({ screenId, children }) {
  const { allowed, loading } = useRbac(screenId);

  // During the location-switch transition, show a neutral spinner so users
  // who will have access at the new location never see "Access Restricted".
  if (loading) return <RolesLoadingSpinner />;

  if (!allowed) return <AccessDenied />;
  return children;
}
